"use client";

import { useState, useEffect } from "react";
import { apiPost, apiAction } from "@/lib/api";
import { useToast } from "@/components/toast-provider";
import { Modal } from "@/components/modal";
import { $n } from "@/lib/format";
import { OPTION_MULTIPLIER } from "@/lib/option-multiplier";
import type { Account, CcLiveData, OpenOrder, DbOptionPosition } from "./types";
import type { ScannedOption } from "./scan-table";
import { ClearableInput } from "@/components/clearable-input";

export function BuyStockModal({ account, buyStock, onClose, onSuccess }: {
  account: Account;
  buyStock: { symbol: string; shares: number } | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { showToast } = useToast();
  const [submitting, setSubmitting] = useState(false);

  return (
    <Modal open={!!buyStock} onClose={() => { if (!submitting) onClose(); }} width="360px" zIndex={60}>
      {buyStock && (
        <div>
          <h3 className="text-sm font-semibold text-green mb-3">Confirm Buy Stock</h3>
          <div className="text-xs space-y-2 mb-4">
            <div className="flex justify-between border-b border-border/50 pb-1">
              <span className="text-muted">Symbol</span>
              <span className="text-foreground font-mono font-medium">{buyStock.symbol}</span>
            </div>
            <div className="flex justify-between border-b border-border/50 pb-1">
              <span className="text-muted">Shares</span>
              <span className="text-foreground font-mono">{buyStock.shares}</span>
            </div>
            <div className="flex justify-between pt-1">
              <span className="text-amber font-medium">Order Type</span>
              <span className="text-foreground font-mono">Market</span>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={onClose}
              disabled={submitting}
              className="text-xs px-3 py-1.5 rounded bg-surface-light text-muted hover:text-foreground transition-colors"
            >
              Cancel
            </button>
            <button
              disabled={submitting}
              onClick={async () => {
                setSubmitting(true);
                const ok = await apiAction(
                  () => apiPost("/options/buy-stock", {
                    accountHash: account.hashValue,
                    tokenId: account.tokenId,
                    symbol: buyStock.symbol,
                    shares: buyStock.shares,
                  }),
                  `Bought ${buyStock.shares} shares of ${buyStock.symbol}`,
                  showToast,
                );
                if (ok) onSuccess();
                setSubmitting(false);
                onClose();
              }}
              className="text-xs px-3 py-1.5 rounded bg-green/20 text-green hover:bg-green/30 transition-colors font-medium disabled:opacity-40"
            >
              {submitting ? "Buying..." : "Buy"}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

export function SellStockModal({ account, target, lastPrice, onClose, onSuccess }: {
  account: Account;
  target: { symbol: string; maxShares: number } | null;
  lastPrice: number | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { showToast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [orderType, setOrderType] = useState<"MARKET" | "LIMIT">("MARKET");
  const [shares, setShares] = useState(0);
  const [price, setPrice] = useState("");
  const [duration, setDuration] = useState("DAY");

  useEffect(() => {
    if (target) {
      setOrderType("MARKET");
      setShares(target.maxShares);
      setPrice(lastPrice != null ? lastPrice.toFixed(2) : "");
      setDuration("DAY");
    }
    // Re-seed only on target change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  const parsedPrice = parseFloat(price);
  const validPrice = orderType === "MARKET" || (!isNaN(parsedPrice) && parsedPrice > 0);
  const max = target?.maxShares ?? 0;

  return (
    <Modal open={!!target} onClose={() => { if (!submitting) onClose(); }} width="420px" zIndex={60}>
      {target && (
        <div>
          <h3 className="text-sm font-semibold text-red mb-3">Sell — {target.symbol}</h3>
          <div className="text-xs space-y-2 mb-4">
            <div className="flex justify-between border-b border-border/50 pb-1">
              <span className="text-muted">Symbol</span>
              <span className="text-foreground font-mono font-medium">{target.symbol}</span>
            </div>
            {lastPrice != null && (
              <div className="flex justify-between border-b border-border/50 pb-1">
                <span className="text-muted">Last</span>
                <span className="text-foreground font-mono">${$n(lastPrice)}</span>
              </div>
            )}
            <div className="flex justify-between items-center border-b border-border/50 pb-1">
              <span className="text-muted">Order Type</span>
              <select
                value={orderType}
                onChange={(e) => setOrderType(e.target.value as "MARKET" | "LIMIT")}
                className="bg-surface-light border border-border rounded px-2 py-0.5 text-foreground font-mono text-xs focus:outline-none focus:border-cyan"
              >
                <option value="MARKET">MARKET</option>
                <option value="LIMIT">LIMIT</option>
              </select>
            </div>
            {orderType === "LIMIT" && (
              <div className="flex justify-between items-center border-b border-border/50 pb-1">
                <span className="text-muted">Limit price</span>
                <div className={`inline-flex items-center rounded border overflow-hidden ${validPrice ? "border-border" : "border-red"}`}>
                  <button type="button" onClick={() => setPrice(Math.max(0.01, (parseFloat(price) || 0) - 0.01).toFixed(2))} className="px-2 py-0.5 text-muted hover:text-foreground hover:bg-surface-light">−</button>
                  <ClearableInput
                    type="text"
                    inputMode="decimal"
                    value={price}
                    onChange={(e) => setPrice(e.target.value.replace(/[^0-9.]/g, ""))}
                    className={`w-16 bg-surface-light border-x border-border px-2 py-0.5 font-mono text-xs text-right focus:outline-none ${validPrice ? "text-foreground" : "text-red"}`}
                  />
                  <button type="button" onClick={() => setPrice(((parseFloat(price) || 0) + 0.01).toFixed(2))} className="px-2 py-0.5 text-muted hover:text-foreground hover:bg-surface-light">+</button>
                </div>
              </div>
            )}
            <div className="flex justify-between items-center border-b border-border/50 pb-1">
              <span className="text-muted">Shares</span>
              <div className="inline-flex items-center rounded border border-border overflow-hidden">
                <button type="button" onClick={() => setShares(Math.max(1, shares - 1))} className="px-2 py-0.5 text-muted hover:text-foreground hover:bg-surface-light">−</button>
                <ClearableInput
                  type="text"
                  inputMode="numeric"
                  value={shares}
                  onChange={(e) => setShares(Math.max(1, Math.min(max, parseInt(e.target.value.replace(/[^0-9]/g, "")) || 1)))}
                  className="w-16 bg-surface-light border-x border-border px-2 py-0.5 text-foreground font-mono text-xs text-right focus:outline-none"
                />
                <button type="button" onClick={() => setShares(Math.min(max, shares + 1))} className="px-2 py-0.5 text-muted hover:text-foreground hover:bg-surface-light">+</button>
              </div>
            </div>
            <div className="flex justify-between items-center pt-1">
              <span className="text-muted">Duration</span>
              <select
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="bg-surface-light border border-border rounded px-2 py-0.5 text-foreground font-mono text-xs focus:outline-none focus:border-cyan"
              >
                <option value="DAY">DAY</option>
                <option value="GTC">GTC</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={onClose}
              disabled={submitting}
              className="text-xs px-3 py-1.5 rounded bg-surface-light text-muted hover:text-foreground transition-colors"
            >
              Cancel
            </button>
            <button
              disabled={submitting || !validPrice || shares < 1}
              onClick={async () => {
                setSubmitting(true);
                try {
                  const res = await apiPost<{ ok: boolean; orderId?: string; error?: string }>(
                    "/options/sell-stock",
                    {
                      accountHash: account.hashValue,
                      tokenId: account.tokenId,
                      symbol: target.symbol,
                      shares,
                      orderType,
                      price: orderType === "LIMIT" ? parsedPrice : undefined,
                      duration,
                    }
                  );
                  if (res.ok) {
                    showToast("success", `Sell placed: ${target.symbol} #${res.orderId}`);
                    onSuccess();
                  } else {
                    showToast("error", res.error ?? "Sell failed");
                  }
                } catch (e) {
                  showToast("error", e instanceof Error ? e.message : "Sell failed");
                }
                setSubmitting(false);
                onClose();
              }}
              className="text-xs px-3 py-1.5 rounded bg-red/20 text-red hover:bg-red/30 transition-colors font-medium disabled:opacity-40"
            >
              {submitting ? "Placing..." : "Place Sell"}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

export function SellCallModal({ account, option, maxContracts, orders, onClose, onSuccess }: {
  account: Account;
  option: ScannedOption | null;
  maxContracts: number;
  orders?: OpenOrder[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { showToast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [qty, setQty] = useState(maxContracts);
  const [price, setPrice] = useState("");

  // Reset qty + price when option changes. Default the limit price to the
  // call mid.
  useEffect(() => {
    if (option) {
      setQty(maxContracts);
      const mid = +((option.bid + option.ask) / 2).toFixed(2);
      setPrice(mid.toFixed(2));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [option?.optionSymbol]);

  const parsedPrice = parseFloat(price);
  const validPrice = !isNaN(parsedPrice) && parsedPrice > 0;

  return (
    <Modal open={!!option} onClose={() => { if (!submitting) onClose(); }} width="420px" zIndex={60}>
      {option && (() => {
        const o = option;
        const mid = +((o.bid + o.ask) / 2).toFixed(2);
        const effectivePrice = validPrice ? parsedPrice : mid;
        const totalCredit = effectivePrice * qty * OPTION_MULTIPLIER;
        return (
          <div>
            <h3 className="text-sm font-semibold text-amber mb-3">Confirm Sell to Open</h3>
            <div className="text-xs space-y-2 mb-4">
              <div className="flex justify-between items-center border-b border-border/50 pb-1">
                <span className="text-muted">Qty</span>
                <div className="inline-flex items-center rounded border border-border overflow-hidden">
                  <button type="button" onClick={() => setQty(Math.max(1, qty - 1))} className="px-2 py-0.5 text-muted hover:text-foreground hover:bg-surface-light">−</button>
                  <ClearableInput
                    type="text"
                    inputMode="numeric"
                    value={qty}
                    onChange={(e) => setQty(Math.max(1, Math.min(maxContracts, parseInt(e.target.value.replace(/[^0-9]/g, "")) || 1)))}
                    className="w-12 bg-surface-light border-x border-border px-2 py-0.5 text-foreground font-mono text-xs text-right focus:outline-none"
                  />
                  <button type="button" onClick={() => setQty(Math.min(maxContracts, qty + 1))} className="px-2 py-0.5 text-muted hover:text-foreground hover:bg-surface-light">+</button>
                </div>
              </div>
              <div className="flex justify-between border-b border-border/50 pb-1">
                <span className="text-muted">Sell</span>
                <span className="text-foreground font-mono">{o.symbol} ${$n(o.strike)} Call · {o.dte}d</span>
              </div>
              <div className="flex justify-between items-center border-b border-border/50 pb-1">
                <span className="text-muted">Limit price</span>
                <div className={`inline-flex items-center rounded border overflow-hidden ${validPrice ? "border-border" : "border-red"}`}>
                  <button type="button" onClick={() => setPrice(Math.max(0.01, (parseFloat(price) || 0) - 0.01).toFixed(2))} className="px-2 py-0.5 text-muted hover:text-foreground hover:bg-surface-light">−</button>
                  <ClearableInput
                    type="text"
                    inputMode="decimal"
                    value={price}
                    onChange={(e) => setPrice(e.target.value.replace(/[^0-9.]/g, ""))}
                    className={`w-16 bg-surface-light border-x border-border px-2 py-0.5 font-mono text-xs text-right focus:outline-none ${validPrice ? "text-foreground" : "text-red"}`}
                  />
                  <button type="button" onClick={() => setPrice(((parseFloat(price) || 0) + 0.01).toFixed(2))} className="px-2 py-0.5 text-muted hover:text-foreground hover:bg-surface-light">+</button>
                </div>
              </div>
              <div className="flex justify-between pt-1">
                <span className="text-green font-medium">Credit</span>
                <span className="text-foreground font-mono font-medium">${$n(effectivePrice)}/sh · ${$n(totalCredit)} total</span>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={onClose}
                disabled={submitting}
                className="text-xs px-3 py-1.5 rounded bg-surface-light text-muted hover:text-foreground transition-colors"
              >
                Cancel
              </button>
              <button
                disabled={submitting || !validPrice}
                onClick={async () => {
                  setSubmitting(true);
                  try {
                    const res = await apiPost<{ ok: boolean; orderId?: string; orderStatus?: string; statusDescription?: string; error?: string }>(
                      "/options/sell",
                      {
                        accountHash: account.hashValue,
                        tokenId: account.tokenId,
                        optionSymbol: o.optionSymbol,
                        quantity: qty,
                        price: +effectivePrice.toFixed(2),
                      }
                    );
                    if (res.ok) {
                      showToast("success", `Sell-to-open ${res.orderStatus ?? "placed"}: ${o.symbol} ${o.description}`);
                      onSuccess();
                    } else {
                      showToast("error", res.error ?? res.statusDescription ?? "Order failed");
                    }
                  } catch (e) {
                    showToast("error", e instanceof Error ? e.message : "Sell-to-open failed");
                  }
                  setSubmitting(false);
                  onClose();
                }}
                className="text-xs px-3 py-1.5 rounded bg-amber/20 text-amber hover:bg-amber/30 transition-colors font-medium disabled:opacity-40"
              >
                {submitting ? "Placing..." : "Place Order"}
              </button>
            </div>
          </div>
        );
      })()}
    </Modal>
  );
}

export function WriteCallModal({ account, option, positionSize, orders, onClose, onSuccess }: {
  account: Account;
  option: ScannedOption | null;
  positionSize: number;
  orders?: OpenOrder[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { showToast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [qty, setQty] = useState(1);
  const [price, setPrice] = useState("");

  // Seed Qty + price when the modal opens for a new option: largest N where
  // N × netDebit × 100 ≤ positionSize. If a working order already exists on
  // this call leg, prefer its price over the computed default.
  useEffect(() => {
    if (option) {
      const mid = (option.bid + option.ask) / 2;
      const netDebit = Math.max(0.01, option.lastPrice - mid);
      const netDebitPerContract = netDebit * OPTION_MULTIPLIER;
      setQty(Math.max(1, Math.floor(positionSize / netDebitPerContract)));
      const lastOrder = (orders ?? [])
        .filter((ord) => ord.optionSymbol === option.optionSymbol)
        .sort((a, b) => b.enteredTime.localeCompare(a.enteredTime))[0];
      setPrice((lastOrder ? lastOrder.price : netDebit).toFixed(2));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [option]);

  const parsedPrice = parseFloat(price);
  const validPrice = !isNaN(parsedPrice) && parsedPrice > 0;

  return (
    <Modal open={!!option} onClose={() => { if (!submitting) onClose(); }} width="420px" zIndex={60}>
      {option && (() => {
        const o = option;
        const mid = +((o.bid + o.ask) / 2).toFixed(2);
        const defaultDebit = +(o.lastPrice - mid).toFixed(2);
        const netDebit = validPrice ? parsedPrice : defaultDebit;
        const perContract = netDebit * OPTION_MULTIPLIER;
        const total = qty * perContract;
        const totalColor = total > positionSize + perContract
          ? "text-red"
          : total > positionSize
          ? "text-amber"
          : "text-foreground";
        return (
          <div>
            <h3 className="text-sm font-semibold text-amber mb-3">Confirm Buy/Write</h3>
            <div className="text-xs space-y-2 mb-4">
              <div className="flex justify-between border-b border-border/50 pb-1">
                <span className="text-muted">Buy {qty * OPTION_MULTIPLIER} shares</span>
                <span className="text-foreground font-mono">{o.symbol} @ ${$n(o.lastPrice)}</span>
              </div>
              <div className="flex justify-between border-b border-border/50 pb-1">
                <span className="text-muted">Sell {qty} call{qty === 1 ? "" : "s"}</span>
                <span className="text-foreground font-mono">${$n(o.strike)} {o.dte}d @ ${$n(mid)} mid</span>
              </div>
              <div className="flex justify-between items-center border-b border-border/50 pb-1">
                <span className="text-muted">Net debit price</span>
                <div className={`inline-flex items-center rounded border overflow-hidden ${validPrice ? "border-border" : "border-red"}`}>
                  <button type="button" onClick={() => setPrice(Math.max(0.01, (parseFloat(price) || 0) - 0.01).toFixed(2))} className="px-2 py-0.5 text-muted hover:text-foreground hover:bg-surface-light">−</button>
                  <ClearableInput
                    type="text"
                    inputMode="decimal"
                    value={price}
                    onChange={(e) => setPrice(e.target.value.replace(/[^0-9.]/g, ""))}
                    className={`w-16 bg-surface-light border-x border-border px-2 py-0.5 font-mono text-xs text-right focus:outline-none ${validPrice ? "text-foreground" : "text-red"}`}
                  />
                  <button type="button" onClick={() => setPrice(((parseFloat(price) || 0) + 0.01).toFixed(2))} className="px-2 py-0.5 text-muted hover:text-foreground hover:bg-surface-light">+</button>
                </div>
              </div>
              <div className="flex justify-between border-b border-border/50 pb-1">
                <span className="text-amber font-medium">Net debit</span>
                <span className="text-foreground font-mono font-medium">${$n(perContract)}</span>
              </div>
              <div className="flex justify-between items-center border-b border-border/50 pb-1">
                <span className="text-muted">Qty</span>
                <div className="inline-flex items-center rounded border border-border overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setQty(Math.max(1, qty - 1))}
                    className="px-2 py-0.5 text-muted hover:text-foreground hover:bg-surface-light"
                  >
                    −
                  </button>
                  <ClearableInput
                    type="text"
                    inputMode="numeric"
                    value={qty}
                    onChange={(e) => setQty(Math.max(1, parseInt(e.target.value.replace(/[^0-9]/g, "")) || 1))}
                    className="w-12 bg-surface-light border-x border-border px-2 py-0.5 text-foreground font-mono text-xs text-right focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setQty(qty + 1)}
                    className="px-2 py-0.5 text-muted hover:text-foreground hover:bg-surface-light"
                  >
                    +
                  </button>
                </div>
              </div>
              <div className="flex justify-between pt-1">
                <span className="text-muted">Total net debit</span>
                <span className={`font-mono ${totalColor}`}>${$n(total)}</span>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={onClose}
                disabled={submitting}
                className="text-xs px-3 py-1.5 rounded bg-surface-light text-muted hover:text-foreground transition-colors"
              >
                Cancel
              </button>
              <button
                disabled={submitting || !validPrice}
                onClick={async () => {
                  setSubmitting(true);
                  try {
                    const res = await apiPost<{ ok: boolean; orderId?: string; orderStatus?: string; statusDescription?: string; error?: string }>(
                      "/options/buy-write",
                      {
                        accountHash: account.hashValue,
                        tokenId: account.tokenId,
                        symbol: o.symbol,
                        optionSymbol: o.optionSymbol,
                        quantity: qty,
                        price: Math.round(netDebit * 100) / 100,
                      }
                    );
                    if (res.ok) {
                      showToast("success", `Buy/write order ${res.orderStatus ?? "placed"}: ${o.symbol} ${o.description}`);
                      onSuccess();
                    } else {
                      showToast("error", res.error ?? res.statusDescription ?? "Order failed");
                    }
                  } catch (e) {
                    showToast("error", e instanceof Error ? e.message : "Buy/write failed");
                  }
                  setSubmitting(false);
                  onClose();
                }}
                className="text-xs px-3 py-1.5 rounded bg-amber/20 text-amber hover:bg-amber/30 transition-colors font-medium disabled:opacity-40"
              >
                {submitting ? "Placing..." : "Place Order"}
              </button>
            </div>
          </div>
        );
      })()}
    </Modal>
  );
}

export function CloseCcModal({ account, position, liveData, decayPct, seedAtMid, beforePlace, onClose, onSuccess }: {
  account: Account;
  position: DbOptionPosition | null;
  liveData: CcLiveData | null;
  decayPct: number;
  /** Seed the BTC price to the current mid instead of the take-profit target. */
  seedAtMid?: boolean;
  /** Awaited right before the close order is placed (e.g. cancel a resting BTC). */
  beforePlace?: () => Promise<void>;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { showToast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [price, setPrice] = useState("");
  const [qty, setQty] = useState(1);
  const [duration, setDuration] = useState("DAY");

  useEffect(() => {
    if (position) {
      const contracts = Math.abs(position.qty);
      const mid = liveData?.mid;
      const decay = Math.max(0.01, +(position.avgOpenPrice * (1 - decayPct / 100)).toFixed(2));
      const target = seedAtMid && mid != null && Number.isFinite(mid)
        ? Math.max(0.01, +mid.toFixed(2))
        : decay;
      setPrice(target.toFixed(2));
      setQty(contracts);
      setDuration("DAY");
    }
    // Intentionally only re-seed on position change — liveData streams updates
    // and would otherwise reset the user's edits every tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position]);

  // Quotes can land after the ticket opens. Fill mid once if the field is still empty.
  useEffect(() => {
    if (!position || !seedAtMid || price) return;
    const mid = liveData?.mid;
    if (mid == null || !Number.isFinite(mid)) return;
    setPrice(Math.max(0.01, +mid.toFixed(2)).toFixed(2));
  }, [position, seedAtMid, liveData?.mid, price]);

  const parsedPrice = parseFloat(price);
  const validPrice = !isNaN(parsedPrice) && parsedPrice > 0;
  const maxContracts = position ? Math.abs(position.qty) : 0;

  return (
    <Modal open={!!position} onClose={() => { if (!submitting) onClose(); }} width="420px" zIndex={60}>
      {position && (() => {
        const p = position;
        const bid = liveData?.bid ?? null;
        const ask = liveData?.ask ?? null;
        const mid = liveData?.mid ?? null;
        return (
          <div>
            <h3 className={`text-sm font-semibold mb-3 ${seedAtMid ? "text-red" : "text-green"}`}>{seedAtMid ? "Exit at mid" : "Exit at stop"} — {p.underlyingSymbol}</h3>
            <div className="text-xs space-y-2 mb-4">
              <div className="flex justify-between border-b border-border/50 pb-1">
                <span className="text-muted">Symbol</span>
                <span className="text-foreground font-mono font-medium">{p.underlyingSymbol}</span>
              </div>
              <div className="flex justify-between border-b border-border/50 pb-1">
                <span className="text-muted">Option</span>
                <span className="text-foreground font-mono text-[10px]">{p.symbol}</span>
              </div>
              {mid != null && (
                <div className="flex justify-between border-b border-border/50 pb-1">
                  <span className="text-muted">Bid / Ask / Mid</span>
                  <span className="font-mono">
                    <span className="text-muted">{bid != null ? `$${$n(bid)}` : "—"}</span>
                    {" / "}
                    <span className="text-muted">{ask != null ? `$${$n(ask)}` : "—"}</span>
                    {" / "}
                    <span
                      className="text-green cursor-pointer"
                      title="Use mid"
                      onClick={() => setPrice(Math.max(0.01, +mid.toFixed(2)).toFixed(2))}
                    >${$n(mid)}</span>
                  </span>
                </div>
              )}
              <div className="flex justify-between items-center border-b border-border/50 pb-1">
                <span className="text-muted">BTC price</span>
                <div className={`inline-flex items-center rounded border overflow-hidden ${validPrice ? "border-border" : "border-red"}`}>
                  <button type="button" onClick={() => setPrice(Math.max(0.01, (parseFloat(price) || 0) - 0.01).toFixed(2))} className="px-2 py-0.5 text-muted hover:text-foreground hover:bg-surface-light">−</button>
                  <ClearableInput
                    type="text"
                    inputMode="decimal"
                    value={price}
                    onChange={(e) => setPrice(e.target.value.replace(/[^0-9.]/g, ""))}
                    className={`w-16 bg-surface-light border-x border-border px-2 py-0.5 font-mono text-xs text-right focus:outline-none ${validPrice ? "text-foreground" : "text-red"}`}
                  />
                  <button type="button" onClick={() => setPrice(((parseFloat(price) || 0) + 0.01).toFixed(2))} className="px-2 py-0.5 text-muted hover:text-foreground hover:bg-surface-light">+</button>
                </div>
              </div>
              <div className="flex justify-between items-center border-b border-border/50 pb-1">
                <span className="text-muted">Qty</span>
                <div className="inline-flex items-center rounded border border-border overflow-hidden">
                  <button type="button" onClick={() => setQty(Math.max(1, qty - 1))} className="px-2 py-0.5 text-muted hover:text-foreground hover:bg-surface-light">−</button>
                  <ClearableInput
                    type="text"
                    inputMode="numeric"
                    value={qty}
                    onChange={(e) => setQty(Math.max(1, Math.min(maxContracts, parseInt(e.target.value.replace(/[^0-9]/g, "")) || 1)))}
                    className="w-12 bg-surface-light border-x border-border px-2 py-0.5 text-foreground font-mono text-xs text-right focus:outline-none"
                  />
                  <button type="button" onClick={() => setQty(Math.min(maxContracts, qty + 1))} className="px-2 py-0.5 text-muted hover:text-foreground hover:bg-surface-light">+</button>
                </div>
              </div>
              <div className="flex justify-between items-center border-b border-border/50 pb-1">
                <span className="text-muted">Duration</span>
                <select
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  className="bg-surface-light border border-border rounded px-2 py-0.5 text-foreground font-mono text-xs focus:outline-none focus:border-cyan"
                >
                  <option value="DAY">DAY</option>
                  <option value="GTC">GTC</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={onClose}
                disabled={submitting}
                className="text-xs px-3 py-1.5 rounded bg-surface-light text-muted hover:text-foreground transition-colors"
              >
                Cancel
              </button>
              <button
                disabled={submitting || !validPrice || qty < 1}
                onClick={async () => {
                  setSubmitting(true);
                  try {
                    if (beforePlace) await beforePlace();
                    const res = await apiPost<{ ok: boolean; orderId?: string; error?: string }>(
                      "/options/cc-btc",
                      {
                        accountHash: account.hashValue,
                        tokenId: account.tokenId,
                        optionSymbol: p.symbol,
                        contracts: qty,
                        price: parsedPrice,
                        duration,
                      }
                    );
                    if (res.ok) {
                      showToast("success", `BTC placed: ${p.underlyingSymbol} #${res.orderId}`);
                      onSuccess();
                    } else {
                      showToast("error", res.error ?? "BTC failed");
                    }
                  } catch (e) {
                    showToast("error", e instanceof Error ? e.message : "BTC failed");
                  }
                  setSubmitting(false);
                  onClose();
                }}
                className={`text-xs px-3 py-1.5 rounded font-medium disabled:opacity-40 transition-colors ${seedAtMid ? "bg-red/20 text-red hover:bg-red/30" : "bg-green/20 text-green hover:bg-green/30"}`}
              >
                {submitting ? "Placing..." : seedAtMid ? "Exit at mid" : "Exit at stop"}
              </button>
            </div>
          </div>
        );
      })()}
    </Modal>
  );
}

/** Build the Schwab order JSON for a replace (PUT) request. */
function buildOrderBody(o: OpenOrder, price: number, qty: number, duration: string, stockMarket: boolean = false): Record<string, unknown> {
  const dur = duration === "GTC" ? "GOOD_TILL_CANCEL" : "DAY";
  const priceStr = price.toFixed(2);

  if (o.orderType === "BUY_WRITE") {
    return {
      orderType: "NET_DEBIT", session: "NORMAL", duration: dur, orderStrategyType: "SINGLE",
      price: priceStr,
      orderLegCollection: [
        { instruction: "BUY", quantity: qty * OPTION_MULTIPLIER, instrument: { symbol: o.underlying, assetType: "EQUITY" } },
        { instruction: "SELL_TO_OPEN", quantity: qty, instrument: { symbol: o.optionSymbol, assetType: "OPTION" } },
      ],
    };
  }
  if (o.orderType === "VERTICAL_SPREAD") {
    return {
      orderType: "NET_CREDIT", session: "NORMAL", duration: dur, orderStrategyType: "SINGLE",
      price: priceStr,
      orderLegCollection: [
        { instruction: "SELL_TO_OPEN", quantity: qty, instrument: { symbol: o.optionSymbol, assetType: "OPTION" } },
        { instruction: "BUY_TO_OPEN", quantity: qty, instrument: { symbol: o.longOptionSymbol!, assetType: "OPTION" } },
      ],
    };
  }
  if (o.orderType === "VERTICAL_SPREAD_CLOSE") {
    return {
      orderType: "NET_DEBIT", session: "NORMAL", duration: dur, orderStrategyType: "SINGLE",
      price: priceStr,
      orderLegCollection: [
        { instruction: "BUY_TO_CLOSE", quantity: qty, instrument: { symbol: o.optionSymbol, assetType: "OPTION" } },
        { instruction: "SELL_TO_CLOSE", quantity: qty, instrument: { symbol: o.longOptionSymbol!, assetType: "OPTION" } },
      ],
    };
  }
  if (o.orderType === "STOCK_SELL" || o.orderType === "STOCK_BUY") {
    const body: Record<string, unknown> = {
      orderType: stockMarket ? "MARKET" : "LIMIT",
      session: "NORMAL", duration: dur, orderStrategyType: "SINGLE",
      orderLegCollection: [
        { instruction: o.orderType === "STOCK_SELL" ? "SELL" : "BUY", quantity: qty, instrument: { symbol: o.underlying, assetType: "EQUITY" } },
      ],
    };
    if (!stockMarket) body.price = priceStr;
    return body;
  }
  // Single-leg: SELL_TO_OPEN or BUY_TO_CLOSE
  return {
    orderType: "LIMIT", session: "NORMAL", duration: dur, orderStrategyType: "SINGLE",
    price: priceStr,
    orderLegCollection: [
      { instruction: o.orderType, quantity: qty, instrument: { symbol: o.optionSymbol, assetType: "OPTION" } },
    ],
  };
}

export function ReplaceOrderModal({ account, order, livePrice, liveBid, liveAsk, onClose, onSuccess, onCancelOrder }: {
  account: Account;
  order: OpenOrder | null;
  livePrice: number | null;
  liveBid: number | null;
  liveAsk: number | null;
  onClose: () => void;
  onSuccess: () => void;
  /** Optional: show a left-side "Cancel Order" control (e.g. mobile Orders). */
  onCancelOrder?: () => void | Promise<void>;
}) {
  const { showToast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [price, setPrice] = useState("");
  const [qty, setQty] = useState(1);
  const [duration, setDuration] = useState("DAY");

  const isStock = order?.orderType === "STOCK_SELL" || order?.orderType === "STOCK_BUY";
  const [stockOrderType, setStockOrderType] = useState<"MARKET" | "LIMIT">("LIMIT");

  // Reset fields when order changes
  useEffect(() => {
    if (order) {
      setPrice(order.price.toFixed(2));
      setQty(isStock ? order.shares : order.quantity);
      setDuration(order.duration === "GOOD_TILL_CANCEL" ? "GTC" : "DAY");
      setStockOrderType(isStock && order.price <= 0 ? "MARKET" : "LIMIT");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  const typeLabel = (o: OpenOrder) => {
    switch (o.orderType) {
      case "BUY_WRITE": return "Buy-Write";
      case "SELL_TO_OPEN": return "Sell to Open";
      case "BUY_TO_CLOSE": return "Buy to Close";
      case "VERTICAL_SPREAD": return "Vertical Spread";
      case "VERTICAL_SPREAD_CLOSE": return "Spread Close";
      case "STOCK_SELL": return "Sell Stock";
      case "STOCK_BUY": return "Buy Stock";
      default: return o.orderType;
    }
  };

  const parsedPrice = parseFloat(price);
  const stockIsMarket = isStock && stockOrderType === "MARKET";
  const validPrice = stockIsMarket || (!isNaN(parsedPrice) && parsedPrice > 0);
  const origQty = order ? (isStock ? order.shares : order.quantity) : 0;
  const origStockType: "MARKET" | "LIMIT" = order && isStock && order.price <= 0 ? "MARKET" : "LIMIT";
  const changed = order ? (
    (!stockIsMarket && parsedPrice !== order.price) ||
    qty !== origQty ||
    (duration === "GTC" ? "GOOD_TILL_CANCEL" : "DAY") !== order.duration ||
    (isStock && stockOrderType !== origStockType)
  ) : false;

  return (
    <Modal open={!!order} onClose={() => { if (!submitting) onClose(); }} width="420px" zIndex={60}>
      {order && (
        <div>
          <h3 className="text-sm font-semibold text-cyan mb-3">Replace Order #{order.orderId}</h3>
          <div className="text-xs space-y-2 mb-4">
            <div className="flex justify-between border-b border-border/50 pb-1">
              <span className="text-muted">Type</span>
              <span className="text-foreground font-mono font-medium">{typeLabel(order)}</span>
            </div>
            <div className="flex justify-between border-b border-border/50 pb-1">
              <span className="text-muted">Symbol</span>
              <span className="text-foreground font-mono font-medium">{order.underlying}</span>
            </div>
            {!isStock && (
              <div className="flex justify-between border-b border-border/50 pb-1">
                <span className="text-muted">Option</span>
                <span className="text-foreground font-mono text-[10px]">{order.optionSymbol}</span>
              </div>
            )}
            {livePrice != null && (
              <div className="flex justify-between border-b border-border/50 pb-1">
                <span className="text-muted">Bid / Ask / Mid</span>
                <span className="font-mono">
                  <span className="text-muted">{liveBid != null ? `$${$n(liveBid)}` : "—"}</span>
                  {" / "}
                  <span className="text-muted">{liveAsk != null ? `$${$n(liveAsk)}` : "—"}</span>
                  {" / "}
                  <span className="text-green">${$n(livePrice)}</span>
                </span>
              </div>
            )}
            {isStock && (
              <div className="flex justify-between items-center border-b border-border/50 pb-1">
                <span className="text-muted">Order Type</span>
                <select
                  value={stockOrderType}
                  onChange={(e) => setStockOrderType(e.target.value as "MARKET" | "LIMIT")}
                  className="bg-surface-light border border-border rounded px-2 py-0.5 text-foreground font-mono text-xs focus:outline-none focus:border-cyan"
                >
                  <option value="MARKET">MARKET</option>
                  <option value="LIMIT">LIMIT</option>
                </select>
              </div>
            )}
            {!stockIsMarket && (
              <div className="flex justify-between items-center border-b border-border/50 pb-1">
                <span className="text-muted">Price</span>
                <div className={`inline-flex items-center rounded border overflow-hidden ${validPrice ? "border-border" : "border-red"}`}>
                  <button type="button" onClick={() => setPrice(Math.max(0.01, (parseFloat(price) || 0) - 0.01).toFixed(2))} className="px-2 py-0.5 text-muted hover:text-foreground hover:bg-surface-light">−</button>
                  <ClearableInput
                    type="text"
                    inputMode="decimal"
                    value={price}
                    onChange={(e) => setPrice(e.target.value.replace(/[^0-9.]/g, ""))}
                    className={`w-16 bg-surface-light border-x border-border px-2 py-0.5 font-mono text-xs text-right focus:outline-none ${validPrice ? "text-foreground" : "text-red"}`}
                  />
                  <button type="button" onClick={() => setPrice(((parseFloat(price) || 0) + 0.01).toFixed(2))} className="px-2 py-0.5 text-muted hover:text-foreground hover:bg-surface-light">+</button>
                </div>
              </div>
            )}
            <div className="flex justify-between items-center border-b border-border/50 pb-1">
              <span className="text-muted">{isStock ? "Shares" : "Qty"}</span>
              <div className="inline-flex items-center rounded border border-border overflow-hidden">
                <button type="button" onClick={() => setQty(Math.max(1, qty - 1))} className="px-2 py-0.5 text-muted hover:text-foreground hover:bg-surface-light">−</button>
                <ClearableInput
                  type="text"
                  inputMode="numeric"
                  value={qty}
                  onChange={(e) => setQty(Math.max(1, parseInt(e.target.value.replace(/[^0-9]/g, "")) || 1))}
                  className="w-16 bg-surface-light border-x border-border px-2 py-0.5 text-foreground font-mono text-xs text-right focus:outline-none"
                />
                <button type="button" onClick={() => setQty(qty + 1)} className="px-2 py-0.5 text-muted hover:text-foreground hover:bg-surface-light">+</button>
              </div>
            </div>
            {!isStock && order.shares > 0 && (
              <div className="flex justify-between border-b border-border/50 pb-1">
                <span className="text-muted">Shares</span>
                <span className="text-foreground font-mono">{qty * OPTION_MULTIPLIER}</span>
              </div>
            )}
            <div className="flex justify-between items-center pt-1">
              <span className="text-muted">Duration</span>
              <select
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="bg-surface-light border border-border rounded px-2 py-0.5 text-foreground font-mono text-xs focus:outline-none focus:border-cyan"
              >
                <option value="DAY">DAY</option>
                <option value="GTC">GTC</option>
              </select>
            </div>
          </div>
          <div className="flex items-center justify-between gap-2">
            <div>
              {onCancelOrder && (
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => { void onCancelOrder(); }}
                  className="text-xs px-3 py-1.5 rounded bg-red/15 text-red hover:bg-red/25 transition-colors font-medium disabled:opacity-40"
                >
                  Cancel Order
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                disabled={submitting}
                className="text-xs px-3 py-1.5 rounded bg-surface-light text-muted hover:text-foreground transition-colors"
              >
                Cancel
              </button>
              <button
                disabled={submitting || !validPrice || !changed}
                onClick={async () => {
                  setSubmitting(true);
                  try {
                    const orderBody = buildOrderBody(order, parsedPrice, qty, duration, stockIsMarket);
                    const res = await apiPost<{ ok: boolean; error?: string }>("/options/replace", {
                      accountHash: order._accountHash,
                      tokenId: order._tokenId,
                      orderId: order.orderId,
                      orderBody,
                    });
                    if (res.ok) {
                      showToast("success", `Order #${order.orderId} replaced`);
                      onSuccess();
                    } else {
                      showToast("error", res.error ?? "Replace failed");
                    }
                  } catch (e) {
                    showToast("error", e instanceof Error ? e.message : "Replace failed");
                  }
                  setSubmitting(false);
                  onClose();
                }}
                className="text-xs px-3 py-1.5 rounded bg-cyan/20 text-cyan hover:bg-cyan/30 transition-colors font-medium disabled:opacity-40"
              >
                {submitting ? "Replacing..." : "Replace"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
